import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { cardSurface } from '@/components/atoms/Card';
import { Spinner } from '@/components/atoms/Spinner';
import { usePermissions } from '@/contexts/auth';
import { type ProviderAccount, useProviderAccounts } from '@/domain/providerConfig';

import { accountLabel } from './accountLabel';
import { EditAccountModal } from './EditAccountModal';

import styles from './DataShares.module.scss';

function statusKey(account: ProviderAccount): string {
    if (!account.supported) return 'providerConfig.accounts.unsupported';
    if (account.rowId === null) return 'providerConfig.accounts.notSetUp';
    return account.isActive ? 'providerConfig.accounts.active' : 'providerConfig.accounts.inactive';
}

/**
 * Every cage-less provider row (S1). They belong to no client, so they have their own internal
 * page rather than a place on each client's; rows the portal has no schema for are shown but not
 * editable.
 */
function AccountConnections() {
    const { t } = useTranslation();
    const { isOptoScaleAdmin } = usePermissions();
    const { accounts, isLoading, error } = useProviderAccounts({ enabled: isOptoScaleAdmin });
    const [editing, setEditing] = useState<string | null>(null);
    const editingAccount = accounts.find((entry) => entry.providerName === editing) ?? null;

    function content() {
        if (error !== null)
            return (
                <Alert tone="danger">
                    {t(
                        error.kind === 'forbidden'
                            ? 'providerConfig.errors.forbidden'
                            : 'providerConfig.accounts.loadFailed'
                    )}
                </Alert>
            );
        if (isLoading) return <Spinner size="lg" />;
        if (accounts.length === 0)
            return <p className={styles.note}>{t('providerConfig.accounts.empty')}</p>;
        return (
            <ul className={styles.accounts}>
                {accounts.map((account) => {
                    const provider = accountLabel(t, account);
                    return (
                        <li
                            key={account.providerName}
                            {...cardSurface({ radius: 'sm', padding: 'none' }, styles.account)}>
                            <span className={styles.accountName}>{provider}</span>
                            <span className={styles.accountStatus}>{t(statusKey(account))}</span>
                            {account.supported && (
                                <Button
                                    variant="secondary"
                                    onClick={() => setEditing(account.providerName)}>
                                    {t(
                                        account.rowId === null
                                            ? 'providerConfig.accounts.setUp'
                                            : 'providerConfig.accounts.edit',
                                        { provider }
                                    )}
                                </Button>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    }

    return (
        <div className={styles.section}>
            {content()}
            {editingAccount !== null && (
                <EditAccountModal
                    key={editingAccount.providerName}
                    account={editingAccount}
                    isOpen
                    onClose={() => setEditing(null)}
                />
            )}
        </div>
    );
}

export default AccountConnections;
export { AccountConnections };
